import { zodResolver } from '@hookform/resolvers/zod';
import {
  buildQuestionnaireAnswerPayload,
  isDefined,
  QuestionnaireAnswerType,
} from '@mfw/common';
import { useIntl } from '@mfw/components';
import { useForm } from '@mfw/forms';
import { z } from 'zod';
import React from 'react';

import type {
  ApiQuestionnaireSubmission,
  SubmitQuestionnaireAnswerArg,
} from '../../schemas/questionnaireSubmissions.schema';
import type { ApiQuestionnaireQuestion } from '../../schemas/questionnaires.schema';

import { useSubmitQuestionnaireAnswers } from '../../hooks/questionnaireSubmissions';
import { useSnackbar } from '../../context';
import messages from './messages';

type FormValues = Record<string, unknown>;

const buildFieldSchema = (
  question: ApiQuestionnaireQuestion,
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
) => {
  switch (question.answerType) {
    case QuestionnaireAnswerType.TEXT:
      // `.trim()` matters: the backend only counts a trimmed-non-empty text
      // as answered, so a whitespace-only value would save fine yet leave the
      // submission IN_PROGRESS — re-opening the blocking dialog with no error
      // in sight.
      return question.isRequired
        ? z
            .string({ message: 'VALIDATION__Required' })
            .trim()
            .min(1, { message: 'VALIDATION__Required' })
        : z.string().optional();
    case QuestionnaireAnswerType.NUMBER:
    case QuestionnaireAnswerType.SCALE: {
      const min =
        question.answerType === QuestionnaireAnswerType.SCALE
          ? question.scaleMin
          : question.numberMin;
      const max =
        question.answerType === QuestionnaireAnswerType.SCALE
          ? question.scaleMax
          : question.numberMax;

      let numberSchema = z.number({ message: 'VALIDATION__Required' });
      if (isDefined(min)) {
        numberSchema = numberSchema.min(min, {
          message: formatMessage(messages.numberTooSmall, { min }),
        });
      }
      if (isDefined(max)) {
        numberSchema = numberSchema.max(max, {
          message: formatMessage(messages.numberTooLarge, { max }),
        });
      }

      return question.isRequired
        ? numberSchema
        : numberSchema.nullable().optional();
    }
    case QuestionnaireAnswerType.YES_NO:
      return question.isRequired
        ? z.enum(['true', 'false'], { message: 'VALIDATION__Required' })
        : z.string().optional();
    case QuestionnaireAnswerType.SINGLE_SELECT:
      return question.isRequired
        ? z.string({ message: 'VALIDATION__Required' }).min(1, {
            message: 'VALIDATION__Required',
          })
        : z.string().optional();
    case QuestionnaireAnswerType.MULTISELECT: {
      const optionsSchema = z.record(z.string(), z.boolean());
      return question.isRequired
        ? optionsSchema.refine((value) => Object.values(value).some(Boolean), {
            message: 'VALIDATION__Required',
          })
        : optionsSchema;
    }
    default:
      return z.any();
  }
};

const buildDefaultValue = (
  question: ApiQuestionnaireQuestion,
  submission: ApiQuestionnaireSubmission,
) => {
  const answer = submission.answers.find(
    (candidate) => candidate.questionId === question.id,
  );
  switch (question.answerType) {
    case QuestionnaireAnswerType.TEXT:
      return answer?.textValue ?? '';
    case QuestionnaireAnswerType.NUMBER:
    case QuestionnaireAnswerType.SCALE:
      return answer?.numberValue ?? null;
    case QuestionnaireAnswerType.YES_NO:
      if (answer?.booleanValue === true) return 'true';
      if (answer?.booleanValue === false) return 'false';
      return '';
    case QuestionnaireAnswerType.SINGLE_SELECT:
      return answer?.selectedOptionIds?.[0] ?? '';
    case QuestionnaireAnswerType.MULTISELECT: {
      const selected = new Set(answer?.selectedOptionIds ?? []);
      return Object.fromEntries(
        (question.options ?? []).map((option) => [
          option.id,
          selected.has(option.id),
        ]),
      );
    }
    default:
      return undefined;
  }
};

export function useQuestionnaireSubmissionForm(
  submission: ApiQuestionnaireSubmission,
  { onSuccess }: { onSuccess?: () => void } = {},
) {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: submitAnswersAsync } = useSubmitQuestionnaireAnswers();

  const questions = React.useMemo(
    () =>
      [...(submission.questionnaire.questions ?? [])].sort(
        (a, b) => a.order - b.order,
      ),
    [submission],
  );

  const schema = React.useMemo(
    () =>
      z.object(
        Object.fromEntries(
          questions.map((question) => [
            question.id,
            buildFieldSchema(question, formatMessage),
          ]),
        ),
      ),
    [questions, formatMessage],
  );

  const methods = useForm({
    defaultValues: Object.fromEntries(
      questions.map((question) => [
        question.id,
        buildDefaultValue(question, submission),
      ]),
    ),
    resolver: zodResolver(schema),
  });

  const handleNext = async (values: FormValues) => {
    const answers = questions
      .map((question) =>
        buildQuestionnaireAnswerPayload(question, values[question.id]),
      )
      .filter((answer): answer is SubmitQuestionnaireAnswerArg =>
        Boolean(answer),
      );

    try {
      await submitAnswersAsync({ id: submission.id, answers });
      onSuccess?.();
    } catch (error) {
      const message =
        (error as { message?: string })?.message ||
        formatMessage(messages.submitError);
      enqueueSnackbar({ message, variant: 'error' });
    }
  };

  return { handleNext, methods, questions };
}
